import { AfterViewInit, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { AtlasClickResultComponent } from '../atlas-click-result.component';
import { Feature, MapBrowserEvent } from 'ol';
import { Geometry } from 'ol/geom.js';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import * as GeoJSONModule from 'ol/format/GeoJSON.js';
const GeoJSON = GeoJSONModule.default as typeof GeoJSONModule.default;
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { AtlasMobileFilterService } from 'src/app/services/filter/atlas-mobile-filter.service';
import { catchError, combineLatest, defer, filter, finalize, map, of, Subject, switchMap, take, timeout } from 'rxjs';
import { unsubscribe } from 'src/app/utils/helpers';

@Component({
    standalone: false,
    selector: 'app-atlas-mobile-map',
    templateUrl: './atlas-mobile-map.component.html',
    styleUrls: ['./atlas-mobile-map.component.scss']
})
export class AtlasMobileMapComponent extends AtlasClickResultComponent implements OnInit, AfterViewInit, OnDestroy {
    private click$ = new Subject<{ event: MapBrowserEvent<MouseEvent>; requestId: number }>();
    private latestByzoneClickRequestId = 0;

    constructor(
        private atlasMobileFilterService: AtlasMobileFilterService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        public olMapService: OLMapService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([
                this.atlasMobileFilterService.atlasMobileService.detailedSubject,
                this.atlasMobileFilterService.atlasMobileService.byzoneSubject,
            ])
                .pipe(
                    filter((loaded) => loaded.every(Boolean)),
                    take(1)
                )
                .subscribe(() => {
                    this.atlasMobileFilterService.setQueryParams();
                    this.atlasMobileFilterService.atlasMobileService.setDefaultFilter();
                    this.cdr.detectChanges();
                })
        );



        this.subscriptions.push(
            this.click$
                .pipe(
                    switchMap(({ event, requestId }) => defer(() => {
                        const endpoint = this.atlasMobileFilterService.atlasMobileService.createJoinEndpoints(event);
                        if (!endpoint) {
                            return of({ requestId, layer: null, fc: null });
                        }

                        return endpoint.featureCollection.pipe(
                            map((fc) => ({ requestId, layer: endpoint.layer, fc }))
                        );
                    }).pipe(
                        timeout(15000),
                        catchError(() => of({ requestId, layer: null, fc: null })),
                        finalize(() => this.zone.run(() => {
                            if (requestId === this.latestByzoneClickRequestId) {
                                this.olMapService.detailsLoading = false;
                                this.olMapService.notifyDetailStateChanged();
                            }
                        }))
                    ))
                )
                .subscribe((result) => {
                    if (result.requestId !== this.latestByzoneClickRequestId) {
                        return;
                    }

                    this.zone.run(() => {
                        this.renderSingleResponse(
                            { layer: result.layer, fc: result.fc },
                            () => this.atlasMobileFilterService.atlasMobileService.removeVectorLayers(),
                            (features) => {
                                const selection = this.getSelectionFeatures(features);
                                this.renderSelection(selection.activeFeature, selection.parentFeature);
                                this.showFeatureDetails(selection.activeFeature, selection.parentFeature);
                            }
                        );
                        this.olMapService.notifyDetailStateChanged();
                        this.cdr.detectChanges();
                    });
                })
        );        this.mapOperations();
    }

    private getSelectionFeatures(features: Feature[]): { activeFeature: Feature<Geometry>; parentFeature?: Feature<Geometry> } {
        if (features.length === 1) {
            return { activeFeature: features[0] };
        }

        const reduced = this.reduceToOnePerLevel(features);
        const selectedLevel = this.atlasMobileFilterService.atlasMobileService.getSelectedAreaLevelForSelection()
            ?? 'statistical_sector';
        const activeFeature = reduced[selectedLevel] ?? features[0];
        const parentFeature = this.getParentFeatureForLevel(reduced, selectedLevel);

        return {
            activeFeature,
            parentFeature,
        };
    }

    private getParentFeatureForLevel(
        reduced: Record<string, Feature<Geometry> | undefined>,
        selectedLevel: string
    ): Feature<Geometry> | undefined {
        const parentCandidates: Record<string, string[]> = {
            statistical_sector: ['municipality', 'province'],
            municipality: ['province', 'region', 'country'],
            arrondissement: ['province', 'region', 'country'],
            province: ['region', 'country'],
            region: ['country'],
            country: [],
        };

        return (parentCandidates[selectedLevel] ?? [])
            .map((level) => reduced[level])
            .find((feature) => !!feature);
    }

    private renderSelection(activeFeature: Feature<Geometry>, parentFeature?: Feature<Geometry>): void {
        if (parentFeature) {
            this.addVectorLayer(parentFeature, new GeoJSON(), true, 'parent');
        }
        this.addVectorLayer(activeFeature, new GeoJSON(), false, 'active');
    }

    private showFeatureDetails(activeFeature: Feature<Geometry>, parentFeature?: Feature<Geometry>): void {
        const coverageValue = this.atlasMobileFilterService.atlasMobileService.getSelectedCoverageValue(activeFeature);
        const name = activeFeature.get('name') ?? activeFeature.get('principal_name');
        const parentName = parentFeature?.get('name') ?? parentFeature?.get('principal_name') ?? parentFeature?.get('alternative_name');

        if (parentName && parentFeature?.get('level') !== 'country') {
            this.olMapService.details.push({
                label: 'MUNICIPALITY',
                value: parentName,
            });
        }

        this.olMapService.details.push({
            label: 'name',
            value: name,
        });
        this.olMapService.details.push({
            label: 'cover',
            value: coverageValue === undefined ? '' : `${(coverageValue * 100).toFixed(2)}%`,
        });
    }

    reduceToOnePerLevel(features: Feature[]): Record<string, Feature<Geometry> | undefined> {
        const levels = ['statistical_sector', 'municipality', 'arrondissement', 'province', 'region', 'country'];
        const byLevel: Record<string, Feature[]> = Object.fromEntries(levels.map((level) => [level, []]));

        features.forEach((f) => {
            const level = f.get('level');
            if (level && byLevel[level]) {
                byLevel[level].push(f);
            }
        });

        const reduce = (level: string): Feature<Geometry> | undefined => {
            const group = byLevel[level];
            if (!group.length) return undefined;

            const seen = new Set<string>();
            const unique = group.filter((f) => {
                const geomStr = new GeoJSON().writeGeometry(f.getGeometry());
                if (seen.has(geomStr)) return false;
                seen.add(geomStr);
                return true;
            });

            return unique[0] as Feature<Geometry>;
        };

        return Object.fromEntries(levels.map((level) => [level, reduce(level)]));
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.atlasMobileFilterService.atlasMobileService.getLegends('mobile-legend');
            this.atlasMobileFilterService.atlasMobileService.getFilter('mobile-filter');
            this.atlasMobileFilterService.atlasMobileService.getDetails('mobile-detail');
        });
    }

    mapOperations(): void {
        this.subscriptions.push(this.olMapService.mapClick$.subscribe((event: MapBrowserEvent<MouseEvent>) => {
            this.zone.run(() => {
                if (!this.isByzoneDatasetActive()) {
                    this.atlasMobileFilterService.atlasMobileService.removeVectorLayers();
                    this.olMapService.closeDetailPanel();
                    this.olMapService.notifyDetailStateChanged();
                    this.cdr.detectChanges();
                    return;
                }

                const requestId = ++this.latestByzoneClickRequestId;
                this.atlasMobileFilterService.atlasMobileService.removeVectorLayers();
                this.olMapService.details = [];
                this.olMapService.detailsLoading = true;
                this.olMapService.notifyDetailStateChanged();
                this.click$.next({ event, requestId });
                this.cdr.detectChanges();
            });
        }));
    }
    private isByzoneDatasetActive(): boolean {
        return this.atlasMobileFilterService.atlasMobileService.getStyleName() !== 'mobile_detailed';
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.atlasMobileFilterService.atlasMobileService.getLayerDetails(feature);
    }

    getLayerName(): string {
        return this.atlasMobileFilterService.atlasMobileService.getLayerName();
    }

    getStyleName(): string {
        return this.atlasMobileFilterService.atlasMobileService.getStyleName();
    }

    loadLayer() {
        this.atlasMobileFilterService.atlasMobileService.loadLayer();
    }

    setFilter() {
        this.atlasMobileFilterService.atlasMobileService.setFilter();
    }

    updateDetails(feature: Feature<Geometry>) {
        this.atlasMobileFilterService.atlasMobileService.updateDetails(feature);
    }

    updateStatSecDetails(feature: Feature<Geometry>) {
        this.atlasMobileFilterService.atlasMobileService.updateStatSecDetails(feature);
    }

    addVectorLayer(feature: Feature<Geometry>, format: any, blackBoundries: boolean = false, role: 'active' | 'parent' = 'active') {
        this.atlasMobileFilterService.atlasMobileService.addVectorLayer(feature, format, blackBoundries, role);
    }

    ngOnDestroy(): void {
        this.atlasMobileFilterService.atlasMobileService.mapOperationsUn();
        this.atlasMobileFilterService.destroy();
        unsubscribe(this.subscriptions);
    }
}